/**
 * @fileoverview View Mode Toggle Component for Planner Module
 * @author MCHD Room Scheduler Team
 * @created 08-10-2025 16:36:00
 * 
 * Segmented control for switching between Day/Week/Month views in planner grid.
 * Provides clear visual feedback for current view mode and keyboard navigation support.
 */

import React from 'react';
import { SegmentedControl, Tooltip } from '@mantine/core';
import { IconCalendar, IconCalendarWeek, IconCalendarMonth } from '@tabler/icons-react';
import type { ViewMode } from '../../utils/dateHelpers';

/**
 * Props for ViewModeToggle component
 */
interface ViewModeToggleProps {
  /** Current view mode */
  value: ViewMode;
  
  /** Callback when view mode changes */
  onChange: (value: ViewMode) => void;
  
  /** Optional size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  
  /** Optional disabled state */
  disabled?: boolean;
  
  /** Optional full width mode */
  fullWidth?: boolean;
}

/**
 * View Mode Toggle Component
 * 
 * Allows coordinators to switch between day/week/month views in the planner.
 * Implements hospital-grade UX with clear labels, tooltips, and keyboard support.
 * 
 * @param props - Component props
 * @returns React component
 * 
 * @example
 * ```tsx
 * <ViewModeToggle
 *   value={viewMode}
 *   onChange={setViewMode}
 *   size="sm"
 * />
 * ```
 * 
 * @created 08-10-2025 16:36:00
 */
export const ViewModeToggle: React.FC<ViewModeToggleProps> = ({
  value,
  onChange,
  size = 'sm',
  disabled = false,
  fullWidth = false
}) => {
  return (
    <Tooltip.Group openDelay={300} closeDelay={100}>
      <SegmentedControl
        value={value}
        onChange={(val) => onChange(val as ViewMode)}
        size={size}
        disabled={disabled}
        fullWidth={fullWidth}
        data={[
          {
            value: 'day',
            label: (
              <Tooltip label="View single day schedule" position="bottom" withArrow>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <IconCalendar size={16} />
                  <span>Day</span>
                </div>
              </Tooltip>
            )
          },
          {
            value: 'week',
            label: (
              <Tooltip label="View 7-day week schedule (Mon-Sun)" position="bottom" withArrow>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <IconCalendarWeek size={16} />
                  <span>Week</span>
                </div>
              </Tooltip>
            )
          },
          {
            value: 'month',
            label: (
              <Tooltip label="View entire month schedule" position="bottom" withArrow>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <IconCalendarMonth size={16} />
                  <span>Month</span>
                </div>
              </Tooltip>
            )
          }
        ]}
        style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          fontWeight: 500
        }}
        aria-label="Select planner view mode"
      />
    </Tooltip.Group>
  );
};

export default ViewModeToggle;

